import { useRequest } from '../../../lib/hooks/useRequest';
import { useRequestFeedback } from '../../../lib/hooks/useRequestFeedBack';
import { request } from '../../../lib/utils/request';
import { ClientSearchResult } from '../../platform/clients/hooks/useClientsSearch';
import { ContentType, AuthorReleaseFormValues, AuthorItem, ReleaseProgress } from '../components/types';

const BASE_URL = process.env.REACT_APP_ADMIN_CONTENT_RELEASE || '';
const CLIENT_CONFIG_URL = process.env.REACT_APP_PLAT_CLIENT_CONFIG_API; //'http://localhost:5010/admin-plat-client-config';
const CONTRACT_API_URL = process.env.REACT_APP_CLIENT_CONTRACT_MANAGEMENT_API; // || 'http://localhost:5010/admin-plat-client-contract-management';
const SEARCH_TAGS_API_URL = process.env.REACT_APP_SEARCH_TAGS_API; // || 'http://localhost:5000/admin-search-tags';

export interface CpfTagResponse {
	cpf: string;
	prefix: string;
	title: string;
	nationality?: string;
	id: string;
}

export const searchTagByCpf = async (cpf: string): Promise<CpfTagResponse | null> => {
	const cleanCpf = cpf.replace(/\D/g, '');
	if (!cleanCpf || cleanCpf.length !== 11) return null;

	try {
		const response = await request().get<CpfTagResponse>(
			`${SEARCH_TAGS_API_URL}/search-tags/by-cpf/${cleanCpf}`
		);
		return response;
	} catch (error) {
		// Se retornar 404 (não encontrado), significa que o CPF está livre
		return null;
	}
};

export function useAuthorReleaseApi() {
	const get_publications = useGetPublications();
	const get_editions = useGetEditions();
	const get_authors = useGetAuthorsByEdition();
	const send_release = useSendRelease(get_publications.result, get_editions.result);

	return {
		get_publications,
		get_editions,
		get_authors,
		send_release,
		loading:
			get_publications.loading || get_editions.loading || get_authors.loading || send_release.loading,
	};
}

// Auxiliares de formatação e geração
export const formatCPF = (value: string): string => {
	if (!value) return '';
	const cleanValue = value.replace(/\D/g, '').slice(0, 11);
	return cleanValue
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const getInitials = (name: string): string => {
	if (!name) return 'EX';
	const parts = name.trim().split(/\s+/);
	if (parts.length === 1) return parts[0].substring(0, 5).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const generateRandomCCode = (): string => {
	return Math.floor(1000000000000 + Math.random() * 9000000000000).toString();
};

const generateContractNumber = (): string => {
	return Math.floor(1000000 + Math.random() * 9000000).toString();
};

// 1. Busca Publicações (Livros ou Revistas)
function useGetPublications() {
	const [result, loading, error, fetch] = useRequest((type: ContentType) => {
		const endpoint = type === 'livro' ? `${BASE_URL}/books/allbooks` : `${BASE_URL}/periodics/allperiodics`;

		return request()
			.get<any[]>(endpoint)
			.then((data) =>
				data.map((item) => ({
					label: item.title || item.name || item.label,
					value: item.id || item.value,
				}))
			);
	});

	useRequestFeedback(null, error);

	return {
		result: result || [],
		loading,
		error,
		get: (type: ContentType, search?: string) => fetch(type, search),
	};
}

// 2. Busca Edições
function useGetEditions() {
	const [result, loading, error, fetch] = useRequest((type: ContentType, publication_id: string) => {
		const isBook = type === 'livro';
		const baseSegment = isBook ? 'books' : 'periodics';

		const endpoint = `${BASE_URL}/${baseSegment}/${publication_id}/editions`;

		return request()
			.get<any>(endpoint)
			.then((res) => {
				const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

				return list.map((item: any) => ({
					label: item.number || item.title || item.name || item.label || `Edição ${item.id}`,
					value: item.id || item.value,
				}));
			});
	});

	useRequestFeedback(null, error);

	return {
		result: result || [],
		loading,
		error,
		get: (typeOrPublicationId: ContentType | string, publication_id?: string) => {
			if (publication_id) {
				return fetch(typeOrPublicationId as ContentType, publication_id);
			}

			const id = typeOrPublicationId as string;
			const inferredType: ContentType = id?.toUpperCase().startsWith('P') ? 'revista' : 'livro';

			return fetch(inferredType, id);
		},
	};
}

// 3. Busca Autores da Edição
function useGetAuthorsByEdition() {
	const [result, loading, error, fetch] = useRequest((type: ContentType, edition_id: string) => {
		const isBook = type === 'livro';
		const baseSegment = isBook ? 'books' : 'periodics';

		const formatted_id =
			edition_id && edition_id.includes('-') ? edition_id.split('-').slice(1).join('-') : edition_id;

		const endpoint = `${BASE_URL}/${baseSegment}/authors/${formatted_id}`;

		return request()
			.get<any>(endpoint)
			.then((res) => {
				const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

				return list.map((item: any) => {
					const rawName = item.title || item.name || item.id || '';
					const cleanName = rawName
						.replace(/^author_author_/, '')
						.replace(/^author_/, '')
						.replace(/-/g, ' ')
						.replace(/\b\w/g, (char: string) => char.toUpperCase());

					const rawCpf = item.keywords?.cpf || item.cpf || '';
					const email = item.keywords?.email || item.email || '';

					return {
						id: item.id,
						name: cleanName,
						cpf: formatCPF(rawCpf),
						email: email,
					};
				});
			});
	});

	useRequestFeedback(null, error);

	return {
		result: result || [],
		loading,
		error,
		get: (typeOrEditionId: ContentType | string, edition_id?: string) => {
			if (edition_id) {
				return fetch(typeOrEditionId as ContentType, edition_id);
			}

			const id = typeOrEditionId as string;
			const inferredType: ContentType = id?.toUpperCase().startsWith('P') ? 'revista' : 'livro';

			return fetch(inferredType, id);
		},
	};
}

function useSendRelease(
	publicationsResult: Array<{ label: string; value: string }> = [],
	editionsResult: Array<{ label: string; value: string }> = []
) {
	const [result, loading, error, send] = useRequest(
		async (data: {
			values: AuthorReleaseFormValues & { edition_title?: string; publication_title?: string };
			onProgress?: (progress: ReleaseProgress) => void;
		}) => {
			const { values, onProgress } = data;
			const allPeople = [...(values.authors || []), ...(values.extra_people || [])];

			let updatedAuthors = values.authors;
			let updatedExtraPeople = values.extra_people;

			if (allPeople.length > 0) {
				// ETAPA 1: Perfis (25%)
				onProgress?.({
					step: 'profiles',
					percent: 25,
					message: 'Criando perfis de autores e pessoas extras...',
				});

				const lastIdRes = await request().get<{ last_id: number }>(`${CLIENT_CONFIG_URL}/last-client-id`);
				const baseLastId = Number(lastIdRes?.last_id || 0);

				const isBook = values.content_type === 'livro';
				const tagPrefix = isBook ? 'book' : 'periodic';
				const publicationId = values.publication_id || '';
				const editionId = values.edition_id || '';

				const cleanEditionId = editionId.includes('-')
					? editionId.split('-').slice(1).join('-')
					: editionId;

				const tagId = `${tagPrefix}_${publicationId}-${cleanEditionId}`;

				const selectedPublication = publicationsResult.find((p) => p.value === publicationId);
				const selectedEdition = editionsResult.find((e) => e.value === editionId);

				const publicationTitle = selectedPublication?.label || values.publication_title || publicationId;
				const editionTitle = selectedEdition?.label || values.edition_title || cleanEditionId;

				let tagTitle = '';
				if (isBook) {
					tagTitle = editionTitle.toLowerCase().includes(publicationTitle.toLowerCase())
						? editionTitle
						: `${publicationTitle} - ${editionTitle}`;
				} else {
					tagTitle = `${publicationTitle} - ${editionTitle}`;
				}

				const tags = [{ id: tagId, title: tagTitle }];

				// 1. Data atual em segundos (10 dígitos)
				const now = Math.floor(Date.now());
				// 2. 100 anos em segundos
				const hundredYearsInSeconds = 100 * 365.25 * 24 * 60 * 60 * 1000;
				const endAt = now + hundredYearsInSeconds;

				let currentIncrement = 0;

				// ETAPA 2: Contratos (50%)
				onProgress?.({
					step: 'contracts',
					percent: 50,
					message: 'Registrando contratos e vinculando tags...',
				});

				const processPersonRegistration = async (
					person: { id?: string; name: string; cpf?: string; email?: string },
					isAuthor: boolean
				): Promise<AuthorItem> => {
					const cleanCpf = person.cpf ? person.cpf.replace(/\D/g, '') : '';
					const BASE_URL = process.env.REACT_APP_PLAT_CLIENT_CONFIG_API;

					let assignedId: string | null = null;

					// 0. BUSCA PRÉVIA: Verifica se já existe um perfil para a pessoa
					try {
						const searchText = person.name;

						const searchBody = {
							text: searchText,
							groups: [],
							page: 1,
							limit: 10,
							sort: 'name',
							sort_dir: 'asc',
						};

						const searchResponse: any = await request().post(
							`${BASE_URL}/public/clients/search`,
							searchBody
						);

						// Suporta tanto a estrutura com índice '0' (vista no log) quanto a chave 'clients' ou array direto
						const clientsList: ClientSearchResult[] =
							searchResponse?.clients ||
							searchResponse?.[0] ||
							(Array.isArray(searchResponse) ? searchResponse : []);

						// Se encontrou algum registro, pega o ID do primeiro retornado
						if (clientsList && clientsList.length > 0) {
							assignedId = clientsList[0].id;
						}
					} catch (error) {
						console.warn('Erro ao realizar busca prévia do cliente:', error);
					}

					// Se NÃO encontrou perfil existente (assignedId continua null), cadastra um novo perfil
					if (!assignedId) {
						currentIncrement += 1;
						assignedId = (baseLastId + currentIncrement).toString();

						// 1. Cadastra Perfil no client-config
						const profilePayload = {
							id: assignedId,
							c_code: isAuthor && cleanCpf.length > 0 ? `C000${cleanCpf}` : generateRandomCCode(),
							name: person.name,
							initials: getInitials(person.name),
							unlimited: false,
							partner: false,
							sale: true,
							search_ai: false,
							copyright: true,
							allContentView: false,
							profile: 1,
							monthlyQuestionLimit: 0,
							enableDomainAccess: false,
						};

						await request().post(`${CLIENT_CONFIG_URL}`, profilePayload);
					}

					// 2. Cadastra Contrato (Executa SEMPRE, vinculado ao assignedId reutilizado ou recém-criado)
					const contractPayload = {
						c_number: generateContractNumber(),
						client_name: person.name,
						comments: '',
						started_at: now,
						end_at: endAt,
						search_ai_forum: false,
						tags: tags,
					};

					await request().post(`${CONTRACT_API_URL}/clients/${assignedId}/contracts`, contractPayload);

					// 3. Atualiza o Autor nas bases DynamoDB e Elasticsearch
					const authorId =
						person.id ||
						`author_${(person.email || person.name).toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

					return {
						id: assignedId,
						name: person.name,
						cpf: person.cpf || '',
						email: person.email || '',
					};
				};

				// const processPersonRegistration = async (
				// 	person: { id?: string; name: string; cpf?: string; email?: string },
				// 	isAuthor: boolean
				// ): Promise<AuthorItem> => {
				// 	currentIncrement += 1;
				// 	const assignedId = (baseLastId + currentIncrement).toString();
				// 	const cleanCpf = person.cpf ? person.cpf.replace(/\D/g, '') : '';

				// 	// 1. Cadastra Perfil no client-config
				// 	const profilePayload = {
				// 		id: assignedId,
				// 		c_code: isAuthor && cleanCpf.length > 0 ? `C000${cleanCpf}` : generateRandomCCode(),
				// 		name: person.name,
				// 		initials: getInitials(person.name),
				// 		unlimited: false,
				// 		partner: false,
				// 		sale: true,
				// 		search_ai: false,
				// 		copyright: true,
				// 		allContentView: false,
				// 		profile: 1,
				// 		monthlyQuestionLimit: 0,
				// 		enableDomainAccess: false,
				// 	};

				// 	await request().post(`${CLIENT_CONFIG_URL}`, profilePayload);

				// 	// 2. Cadastra Contrato
				// 	const contractPayload = {
				// 		c_number: generateContractNumber(),
				// 		client_name: person.name,
				// 		comments: '',
				// 		started_at: now,
				// 		end_at: endAt,
				// 		search_ai_forum: false,
				// 		tags: tags,
				// 	};

				// 	await request().post(`${CONTRACT_API_URL}/clients/${assignedId}/contracts`, contractPayload);

				// 	// 3. NOVO PASSO: Atualiza o Autor nas bases DynamoDB e Elasticsearch do admin-content-release
				// 	// Se a pessoa tiver um 'id' vindo da busca (ex: author_jose-silva-aires), usamos ele; caso contrário, geramos a chave.
				// 	const authorId =
				// 		person.id ||
				// 		`author_${(person.email || person.name).toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

				// 	return {
				// 		id: assignedId,
				// 		name: person.name,
				// 		cpf: person.cpf || '',
				// 		email: person.email || '',
				// 	};
				// };

				if (values.authors && values.authors.length > 0) {
					updatedAuthors = await Promise.all(
						values.authors.map((author) => processPersonRegistration(author, true))
					);
				}

				if (values.extra_people && values.extra_people.length > 0) {
					updatedExtraPeople = await Promise.all(
						values.extra_people.map((extra) => processPersonRegistration(extra, false))
					);
				}

				// ETAPA 3: Enviando E-mails via Amazon SES (75%)
				// ETAPA 3: Notificação por E-mail (75%)
				// ETAPA 3: Envio de E-mails via Amazon SES (75%)
				// Função para gerar o slug do autor exatamente no padrão da plataforma
				const buildAuthorSlug = (name?: string, email?: string): string => {
					if (name && name.trim()) {
						const cleanName = name
							.toLowerCase()
							.trim()
							.normalize('NFD')
							.replace(/[\u0300-\u036f]/g, '') // Remove acentos
							.replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
							.replace(/\s+/g, '-'); // Espaços viram hífens

						return `author_${cleanName}`;
					}

					// Fallback seguro usando e-mail caso não haja nome
					const cleanEmail = (email || '')
						.toLowerCase()
						.trim()
						.replace(/[^a-z0-9]/g, '-');
					return `author_${cleanEmail}`;
				};

				// ... no bloco de envio:
				onProgress?.({
					step: 'emails',
					percent: 75,
					message: 'Enviando notificações aos cadastrados...',
				});

				const emailRecipients = [
					...(updatedAuthors || []).map((a) => ({
						id: buildAuthorSlug(a.name, a.email), // Gera a slug author_...
						name: a.name,
						email: a.email,
					})),
					...(updatedExtraPeople || []).map((e) => ({
						id: buildAuthorSlug(e.name, e.email), // Gera a slug author_...
						name: e.name,
						email: e.email,
					})),
				].filter((r) => r.email && r.email.includes('@'));

				if (emailRecipients.length > 0) {
					try {
						await request().post(`${BASE_URL}/send-mail`, {
							recipients: emailRecipients,
							publication_title: publicationTitle,
							content_type: values.content_type,
						});
					} catch (emailError) {
						console.warn(
							'Aviso: Erro ao disparar e-mails via SES, prosseguindo com a liberação.',
							emailError
						);
					}
				}
				// ETAPA 4: Conclusão Final (90%)
				onProgress?.({
					step: 'release',
					percent: 100,
					message: 'Finalizando a liberação do conteúdo...',
				});
			}

			// const payloadRelease = {
			// 	...values,
			// 	authors: updatedAuthors,
			// 	extra_people: updatedExtraPeople,
			// };

			// // Post realizado na URL atual da página
			// const releaseResponse = await request().post(window.location.href, payloadRelease);

			// // Concluído (100%)
			// onProgress?.({
			// 	step: 'done',
			// 	percent: 100,
			// 	message: 'Liberação e contratos concluídos com sucesso!',
			// });

			return true;
		},
		false
	);

	useRequestFeedback(null, error);

	return {
		result,
		loading,
		error,
		send: (
			values: AuthorReleaseFormValues & { edition_title?: string; publication_title?: string },
			onProgress?: (progress: ReleaseProgress) => void
		) => send({ values, onProgress }),
	};
}
